<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    :no-title="isNoTitle"
    button-location="top"
    @submit="getTransactions()"
  >
    <template #title>
      All Transactions
    </template>

    <template #top-form>
      <div
        v-if="!isFraudCheck && !isPartnerDashboard"
        class="row align-items-center"
      >
        <label class="col-sm-4 control-label">Search Type*</label>
        <div class="col-sm-4">
          <dropdown-list
            v-model="searchConditions.type"
            :options="searchTypeList"
          />
        </div>
      </div>

      <div
        v-if="isPhoneNumberFieldShow"
        class="row align-items-center"
      >
        <label class="col-sm-4 control-label">Phone Number*</label>
        <phone-number-input
          id="customer-phone"
          v-model="searchConditions.phoneNumber"
          :page-id="pageId"
          :required="true"
          class="col-sm-4"
        />
      </div>

      <div
        v-if="isPartnerCodeFieldShow"
        class="row align-items-center"
      >
        <label class="col-sm-4 control-label">Partner Code*</label>
        <div class="col-sm-4">
          <sanitized-input
            id="partnerCodeName"
            v-model="searchConditions.partnerCodeName"
            placeholder="Partner Code Name"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="control-label col-sm-4">Category*</label>
        <div class="col-sm-4">
          <dropdown-list
            v-model="searchConditions.category"
            :options="categoryList"
            value-key="id"
          />
        </div>
      </div>

      <div class="row align-items-center">
        <label class="control-label col-sm-4">Status Type*</label>
        <div class="col-sm-4">
          <dropdown-list
            v-model="searchConditions.status"
            :options="statusTypeList"
          />
        </div>
      </div>

      <div
        v-if="!isFraudCheck"
        class="row align-items-center"
      >
        <label class="col-sm-4 control-label">Date Range</label>
        <div class="col-sm-5">
          <date-picker
            v-model="timeSpanValue"
            type="daterange"
          />
        </div>
      </div>

      <div class="form-submit-button-group pb-10 mobile-pb-10">
        <p-button
          :disabled="status.isLoading || !isValidSearchInput"
          native-type="submit"
          round
          type="purple"
        >
          Get Transactions
        </p-button>

        <p-button
          v-if="!isFraudCheck"
          :disabled="status.isLoading || !isValidSearchInput"
          round
          type="info"
          @click="downloadTransactionsToCSV()"
        >
          Download
        </p-button>

        <p-button
          v-if="!isFraudCheck"
          :disabled="status.isLoading"
          round
          type="yellow"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>

      <div class="row align-items-center">
        <pagination
          :current-page="paginationInfo.currentPage"
          :is-loading="status.isLoading"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="getTransactions"
        />
      </div>
      <message-html :result-html="status.resultHtml" />
    </template>

    <template #content>
      <table-view-container
        :custom-cell-style-func="customCellStyle"
        :data="transactions"
        :get-summaries="(param) => getSummaries(param, ['amount', 'customerCredits', 'customerCharge', 'partnerCredits', 'partnerCharge'])"
        :highlight-list="failedTransactionsIndexes"
        :is-summary="true"
        :page-id="pageId"
        :selected-index="status.selectedIndex"
        :table-columns="tableColumns"
        @column-click="(row, column) => gotoRideHistoryPage(row, column)"
      >
        <template
          v-if="!isFraudCheck && !isPartnerDashboard"
          #right-fixed-columns
        >
          <el-table-column
            v-if="transactions.length > 0"
            fixed="right"
            label="Info"
            width="50"
          >
            <template #default="scope">
              <div
                :class="scope.$index === status.selectedIndex ? 'active' : ''"
                class="info-icon border"
                @click="getTransactionDistribution(scope)"
              >
                <i class="fa fa-info" />
              </div>
            </template>
          </el-table-column>
          <el-table-column
            v-if="transactions.length > 0 && hasSpecialPermission"
            fixed="right"
            label="Refund"
            width="60"
          >
            <template #default="scope">
              <div
                v-if="transactionUtility.getRefundFormRouteByTransaction(scope.row)"
                :class="scope.$index === status.selectedIndex ? 'active' : ''"
                class="refund-icon"
                @click="gotoRefundForm(scope.row)"
              >
                <i class="fa fa-money" />
              </div>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>
  </card-container>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, Ref, ref, toRefs, watch } from 'vue';
import { LocationQuery, useRoute, useRouter } from 'vue-router';
import Moment from 'moment-timezone';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useDashboard } from '@/use/app';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import { usePayment } from '@/use/payment';
import transactionUtility from '@/Utils/transactionUtility';
import { ChargeType } from '@/models/transaction';
import { TableColumnType, HighlightTableItemType } from '@/models/table';
import { ListType } from '@/models/app';
import { TransactionType, TransactionQueryInput } from '@/models/transaction';
import { useProfile } from '@/use/profile';
import { transactionService } from '@/services/transaction';

interface Total {
  amount: number;
  customerCredits: number;
  customerCharge: number;
  partnerCredits: number;
  partnerCharge: number;
  expiringCredits: number;
}

const pageId = 'transaction-list';
const paginationLimit = 10;
const statusTypeList: ListType[] = [
  { id: 1, key: '', label: 'All Status' },
  { id: 2, key: 'success', label: 'Success' },
  { id: 3, key: 'failed', label: 'Failed' },
];
const searchTypeList: ListType[] = [
  { id: 1, key: 'phoneNumber', label: 'Phone Number' },
  { id: 2, key: 'partnerCodeName', label: 'Partner Code Name' },
];
const categoryList: ListType[] = [
  { id: 0, key: 'all', label: 'All Categories' },
  { id: ChargeType.Ride, key: 'ride', label: ChargeType[ChargeType.Ride] },
  { id: ChargeType.Food, key: 'food', label: ChargeType[ChargeType.Food] },
  { id: ChargeType.HomeServices, key: 'homeServices', label: ChargeType[ChargeType.HomeServices] },
  { id: ChargeType.FoodExtraCharge, key: 'foodExtraCharge', label: ChargeType[ChargeType.FoodExtraCharge] },
  { id: ChargeType.Groceries, key: 'groceries', label: ChargeType[ChargeType.Groceries] },
  { id: ChargeType.D2DScheduledRide, key: 'd2dScheduledRide', label: ChargeType[ChargeType.D2DScheduledRide] },
  { id: ChargeType.CreditSale, key: 'creditSale', label: ChargeType[ChargeType.CreditSale] },
  { id: ChargeType.Membership, key: 'membership', label: ChargeType[ChargeType.Membership] },
  { id: ChargeType.Refund, key: 'refund', label: ChargeType[ChargeType.Refund] },
  { id: ChargeType.Referral, key: 'referral', label: ChargeType[ChargeType.Referral] },
  { id: ChargeType.Promo, key: 'promo', label: ChargeType[ChargeType.Promo] },
  { id: ChargeType.Other, key: 'other', label: ChargeType[ChargeType.Other] },
];

const props = defineProps({
  isNoTitle: {
    type: Boolean,
    default: false,
  },
  isFraudCheck: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();
const route = useRoute();

const { customerInfo, customerPhoneNumber } = useCustomer();
const { isPartnerDashboard } = useDashboard();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
const { partnerId, partnerCode } = usePartner();
const { setTransactionId } = usePayment();
const { userRoles } = useProfile();

const { isAdmin, isSupervisor, isDirector, isTeamLead } = toRefs(userRoles);

const hasSpecialPermission = computed(() => {
  return isAdmin.value || isSupervisor.value || isDirector.value || isTeamLead.value;
});

const transactions: Ref<TransactionType[]> = ref([]);
const transactionsTotal: Ref<Total> = ref({
  amount: 0,
  customerCredits: 0,
  customerCharge: 0,
  partnerCredits: 0,
  partnerCharge: 0,
  expiringCredits: 0,
});
const failedTransactionsIndexes: Ref<HighlightTableItemType[]> = ref([]);
const tableColumns: Ref<TableColumnType[]> = ref([]);
const searchConditions = reactive({
  type: '',
  phoneNumber: '',
  partnerCodeName: '',
  status: '',
  category: 0,
  startDate: '',
  endDate: '',
});
const timeSpanValue: Ref<any> = ref(null);
const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  resultHtml: '',
  selectedIndex: -1,
});
const paginationInfo = reactive({
  currentPage: 1,
  totalPages: 1,
});

const isValidSearchInput = computed(() => {
  if (searchConditions.type === 'phoneNumber') {
    return util.isPhoneNumberValid(searchConditions.phoneNumber);
  }

  return !!searchConditions.partnerCodeName;
});

const isPhoneNumberFieldShow = computed(() => !props.isFraudCheck && !isPartnerDashboard.value && searchConditions.type === 'phoneNumber');

const isPartnerCodeFieldShow = computed(
  () => !props.isFraudCheck && !isPartnerDashboard.value && searchConditions.type === 'partnerCodeName',
);

const columnsForOperators = computed(() => {
  if (isPartnerDashboard.value) {
    return [];
  }

  return [
    { field: 'ipAddress', label: 'IP Address', width: 120 },
    { field: 'ipCountry', label: 'IP Country', width: 120 },
  ];
});

const initTableColumns = () => {
  tableColumns.value = [
    { field: 'id', label: 'Transaction ID', width: 200 },
    { field: 'createdAt', label: 'Date', width: 180, isDateField: true },
    { field: 'categoryText', label: 'Category', width: 90 },
    { field: 'currency', label: 'Currency', width: 90 },
    { field: 'amount', label: 'Total', width: 100 },
    { field: 'customerCredits', label: 'Customer Credits', width: 140 },
    { field: 'expiringCredits', label: 'Expiring Credits', width: 140 },
    { field: 'customerCharge', label: 'Customer Charge', width: 140 },
    { field: 'partnerCredits', label: 'Partner Credits', width: 120 },
    { field: 'partnerCharge', label: 'Partner Charge', width: 130 },
    { field: 'statusText', label: 'Status', width: 180, isCapitalize: true },
    { field: 'description', label: 'Description', width: 250, formatter: formatDescription },
    { field: 'notes', label: 'notes', width: 250 },
    { field: 'associatedObjectType', label: 'Type', width: 100 },
    { field: 'associatedObjectId', label: 'Associated Object ID', width: 200 },
    ...columnsForOperators.value,
    { field: 'device', label: 'Device', width: 100 },
    { field: 'operatingSystem', label: 'Operating System', width: 150 },
    { field: 'browser', label: 'Browser', width: 120 },
    { field: 'userName', label: 'User Name', width: 150 },
    { field: 'role', label: 'role', width: 120 },
  ];
};

const getTransactions = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;
  paginationInfo.currentPage = currentPage;
  status.resultHtml = '';
  status.selectedIndex = -1;
  status.spinnerMessage = 'Loading Transactions...';
  failedTransactionsIndexes.value = [];

  status.isLoading = true;
  try {
    const queryParams = generateQueryParams();

    const response = await transactionService.fetchTransactions({
      ...queryParams,
      pagination: {
        limit: paginationLimit,
        offset,
      },
    });

    transactionsTotal.value = response.total;
    transactions.value = response.rows.map((transaction: TransactionType, index: number) => {
      if (transaction.status === 'failed') {
        failedTransactionsIndexes.value.push({ index, class: 'text-red', type: 'row' });
      }

      transaction.orderIndex = index;
      return transactionUtility.formatTransactionData(transaction);
    });
    paginationInfo.totalPages = Math.ceil(response.count / paginationLimit) || 1;
  } catch (error) {
    transactions.value = [];
    paginationInfo.currentPage = 1;
    paginationInfo.totalPages = 1;
    status.resultHtml = util.getGraphqlError(error);
  }
  status.isLoading = false;
};

const getSummaries = (params: any, summaryColumns: string[], summaryTitle: string = 'Total') => {
  const sums: string[] = [];
  const total: Total = transactionsTotal.value;

  const totalMapping: Total = {
    amount: total.amount,
    customerCredits: total.customerCredits,
    customerCharge: total.customerCharge,
    partnerCredits: total.partnerCredits,
    partnerCharge: total.partnerCharge,
    expiringCredits: total.expiringCredits,
  };

  params.columns.forEach((column: any, index: number) => {
    if (index === 0) {
      sums[index] = summaryTitle;
    } else if (summaryColumns.includes(column.property)) {
      const value = totalMapping[column.property as keyof Total];
      sums[index] = value !== undefined ? `$ ${value.toFixed(2)}` : '';
    } else {
      sums[index] = '';
    }
  });

  return sums;
};

const getTransactionDistribution = (scope: { $index: number; row: TransactionType }) => {
  status.selectedIndex = scope.$index;

  setTransactionId(scope.row.id);
};

const downloadTransactionsToCSV = async () => {
  status.spinnerMessage = 'Preparing Transactions to Download...';
  status.isLoading = true;

  try {
    const queryParams = generateQueryParams();
    const transactionsCsv = await transactionService.downloadTransactions(queryParams);

    util.downloadJSONToCSV(transactionsCsv, 'Transactions.csv');

    showSuccessPopupDialog({
      text: 'The transactions have been downloaded successfully!',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  status.isLoading = false;
};

const generateQueryParams = () => {
  const queryParams: TransactionQueryInput = {
    status: searchConditions.status,
    category: searchConditions.category,
    startDate: searchConditions.startDate,
    endDate: searchConditions.endDate,
  };

  // make query regarding with search type
  if (searchConditions.type === 'phoneNumber') {
    queryParams.phoneNumber = searchConditions.phoneNumber;
  } else if (searchConditions.type === 'partnerCodeName') {
    queryParams.partnerCodeName = searchConditions.partnerCodeName;
  }

  return queryParams;
};

// format description for gourmet
const formatDescription = (description: string): string => {
  if (!description) {
    return '';
  }

  const foodMatch = description.match(/Food: (.*)/);
  if (foodMatch) {
    const link = isPartnerDashboard.value ? `/partner/delivery-orders/${foodMatch[1]}` : `/operator/delivery-order-history?gourmetOrderId=${foodMatch[1]}#gourmet-history`;
    return `CustomCharge - Food: <a class="order-link" target="_blank" href="${link}">${foodMatch[1]}</a>`;
  }

  return description;
};

const resetForm = () => {
  status.resultHtml = '';
  paginationInfo.currentPage = 1;
  paginationInfo.totalPages = 1;
  transactions.value = [];
  failedTransactionsIndexes.value = [];
  timeSpanValue.value = null;

  searchConditions.type = isPartnerDashboard.value ? 'partnerCodeName' : 'phoneNumber';
  searchConditions.phoneNumber = '';
  searchConditions.status = '';
  searchConditions.category = Number(categoryList[0].id);
  searchConditions.partnerCodeName = isPartnerDashboard.value ? partnerId.value : '';
};

const gotoRideHistoryPage = (row: TransactionType, column: any) => {
  if (column.property !== 'associatedObjectId') {
    return;
  }

  if (row.associatedObjectType !== 'Ride') {
    return;
  }

  const query: LocationQuery = route.query || {};
  query.requestId = row.associatedObjectId ?? '';

  router.push({
    name: 'RideHistory',
    query,
  });
};

const customCellStyle = (param: any) => {
  const { column, row } = param;

  if (column.property !== 'associatedObjectId') {
    return;
  }

  if (row.associatedObjectType !== 'Ride') {
    return;
  }

  return {
    cursor: 'pointer',
    color: '#00F',
  };
};

const gotoRefundForm = (transaction: TransactionType) => {
  const refundForm = transactionUtility.getRefundFormRouteByTransaction(transaction, route.query);

  if (!refundForm) {
    return;
  }

  router.push(refundForm);
};

watch(customerInfo, () => {
  if (isPartnerDashboard.value) {
    return;
  }

  resetForm();

  searchConditions.phoneNumber = customerPhoneNumber.value;

  if (searchConditions.phoneNumber) {
    getTransactions();
  }
});

watch(timeSpanValue, (value: any) => {
  if (value) {
    searchConditions.startDate = util.formatDateTime(value[0], 'YYYY-MM-DD HH:mm:ss');
    searchConditions.endDate = util.formatDateTime(Moment(value[1]).endOf('day'), 'YYYY-MM-DD HH:mm:ss');
  } else {
    searchConditions.startDate = '';
    searchConditions.endDate = '';
  }
});

onMounted(() => {
  initTableColumns();

  // set defatult search type regarding with dashboard
  // in partner dashboard, default type is partnerCode
  // in operator dashboard, default type is phone number
  if (isPartnerDashboard.value) {
    searchConditions.type = 'partnerCodeName';
    searchConditions.partnerCodeName = partnerCode.value;
  } else {
    searchConditions.type = 'phoneNumber';
    searchConditions.phoneNumber = customerPhoneNumber.value;
  }

  if (searchConditions.phoneNumber || isPartnerDashboard.value) {
    getTransactions();
  }
});
</script>

<style lang="scss">
.transaction-list {
  .refund-icon {
    width: 30px;
    height: 30px;
    font-size: 22px;
    line-height: 28px;
    cursor: pointer;
    margin: 0 auto;
    text-align: center;
    padding-left: 2px;
    color: #ee682d;
  }
}
</style>
